import { useEffect, useRef } from "react";
import { TRACKING_VERSION } from "./config";
import { postEvents, postHeartbeat } from "./client";

function uuid() {
    return crypto.randomUUID?.() || Math.random().toString(36).slice(2);
}

export function useTotemAnalytics(ids) {
    const sessionIdRef = useRef(uuid());
    const queueRef = useRef([]);
    const flushingRef = useRef(false);
    const hbTimerRef = useRef(null);

    const base = () => ({
        session_id: sessionIdRef.current,
        totem_id: ids.totem_id,
        store_id: ids.store_id,
        v: TRACKING_VERSION,
    });

    const push = (type, payload = {}) => {
        queueRef.current.push({
            type,
            payload,
            ...base(),
            timestamp: new Date().toISOString(),
        });
        flush();
    };

    const flush = async () => {
        if (flushingRef.current || queueRef.current.length === 0) return;
        flushingRef.current = true;
        const batch = queueRef.current.splice(0, queueRef.current.length);
        try {
            console.log("[analytics] sending batch", batch);
            await postEvents(ids.totem_id, batch);
            console.log("[analytics] OK");
        } catch (e) {
            console.error("[analytics] ERROR sending batch:", e);
            queueRef.current.unshift(...batch);
        } finally {
            flushingRef.current = false;
        }
    };

    const startHeartbeat = () => {
        stopHeartbeat();
        const t = setInterval(() => {
            postHeartbeat(ids.totem_id, {
                totem_id: ids.totem_id,
                store_id: ids.store_id,
            }).catch(() => {});
        }, 30000);
        hbTimerRef.current = t;
    };

    const stopHeartbeat = () => {
        if (hbTimerRef.current) {
            clearInterval(hbTimerRef.current);
            hbTimerRef.current = null;
        }
    };

    useEffect(() => {
        push("session_start", { entrypoint: window.location.pathname });
        startHeartbeat();

        const onBeforeUnload = () => {
            push("session_end", { reason: "exit" });
            flush();
        };
        window.addEventListener("beforeunload", onBeforeUnload);

        return () => {
            window.removeEventListener("beforeunload", onBeforeUnload);
            stopHeartbeat();
        };
    }, []);

    return {

        trackCategoryView: (category_id, extra = {}) =>
            push("category_view", { category_id, ...extra }),

        trackProductClick: (product_id, extra = {}) =>
            push("product_click", { product_id, ...extra }),

        trackCart: (type, payload) => push(type, payload),

        trackAffiliation: (channel) =>
            push("user_affiliated", { channel }),

        // ⭐ NEW
        trackIdleStart: () => push("idle_start"),

        // ⭐ NEW
        trackIdleEnd: (seconds) =>
            push("idle_end", { idle_seconds: seconds }),

        sessionEnd: () => {
            push("session_end", { reason: "start_idle" });
        },

        sessionStart: () => {
            sessionIdRef.current = uuid();
            push("session_start", { entrypoint: "reset" });
        },

        newSession: () => {
            push("session_end", { reason: "manual_reset" });
            sessionIdRef.current = uuid();
            push("session_start", { entrypoint: "reset" });
        },
    };
}
